/**
 * Post-Checkout Box Builder
 * Handles the customization page after checkout
 *
 * Everyone is on Rotating Curations, so there is no plan to choose:
 * the page opens on Step 2 (dietary + curation selector + pre-filled items) → Complete
 */

class PostCheckoutBoxBuilder {
  constructor(config) {
    this.config = {
      apiBaseUrl: config.apiBaseUrl || '/apps/box-builder',
      orderId: this.getOrderIdFromUrl(),
      orderToken: this.getTokenFromUrl(),
      curatedRedirectUrl: config.curatedRedirectUrl || '/pages/box-customized?status=curated',
      primaryProducts: config.primaryProducts || {
        'standard': '9737594896664',
        'no-meat': '10031773253912',
        'no-nuts': '10035333071128',
        'no-crackers': '10035260129560'
      },
      themeDisplayNames: config.themeDisplayNames || {},
      themeDescriptions: config.themeDescriptions || {},
      texts: config.texts || {},
      // How long to wait for the page bootstrap's /init before sending our own
      initWaitMs: config.initWaitMs || 20000
    };
    
    this.state = {
      // Session & Flow
      sessionId: null,
      currentStep: null,
      status: 'pending',
      
      // Order & Box Config (from /init)
      orderId: null,
      orderNumber: null,
      boxSize: 'M',
      maxItems: 7,
      shipDate: null,
      shipWeek: null,
      boxSizePricing: null,

      // Step 2: Preferences & Items
      curationType: 'rotating',
      theme: null,
      dietary: 'standard',
      availableThemes: [],
      variantData: null, // Full variant data from /variants API
      
      // Items
      availableItems: [],
      selectedItems: [],
      collections: [],
      
      // UI State
      isLoading: false,
      error: null
    };
    
    this.elements = {};
    this.init();
  }

  // ============================================================
  // INITIALIZATION
  // ============================================================
  
  async init() {
    try {
      this.cacheElements();
      
      // Move footer to document.body so position:fixed works
      // regardless of ancestor transforms in Shopify themes
      if (this.elements.footer) {
        document.body.appendChild(this.elements.footer);
      }
      
      this.setupEventListeners();
      
      if (!this.config.orderId) {
        console.error('[BoxBuilder] No order ID found');
        this.showError('ORDER_NOT_FOUND', 'No order ID provided. Please check your link.');
        return;
      }
      
      const restored = this.restoreFromStorage();
      
      this.showLoading(this.config.texts.loadingInit || 'Loading your order...');
      await this.initSession();

      // If the order routes to the TRAY flow, bail out — PostCheckoutTrayBuilder
      // handles it. The bootstrap has already revealed the tray section.
      if (this.state.bailedOutForTray) {
        this.hideLoading();
        return;
      }

      // A fresh visit starts on the default curation; a reload keeps the one picked
      if (!restored) {
        this.state.theme = null;
      }
      await this.enterRotatingCurations();

      this.hideLoading();
      this.configureStep2();
      this.showStep(2);
      this.updateStep2Displays();

    } catch (error) {
      console.error('[BoxBuilder] Initialization failed:', error);
      this.hideLoading();
      // Preserve the original API error code (e.g. BUNDLE_NOT_READY) so
      // showError can pick a friendlier title for known cases.
      const errorCode = (error && error.code) || 'INIT_ERROR';
      this.showError(errorCode, error.message || 'Failed to initialize. Please refresh the page.');
    }
  }

  cacheElements() {
    this.elements = {
      container: document.querySelector('.post-checkout-box-builder'),
      loadingOverlay: document.getElementById('loading-overlay'),
      loadingMessage: document.querySelector('.loading-overlay__message'),
      errorState: document.getElementById('error-state'),
      errorMessage: document.getElementById('error-message'),
      orderNumber: document.getElementById('order-number'),
      boxSizeLabel: document.getElementById('box-size-label'),
      shipWeek: document.getElementById('ship-week'),
      dietaryInputs: document.querySelectorAll('input[name="dietary"]'),
      curationSelector: document.getElementById('curation-selector'),
      productGrid: document.getElementById('product-grid'),
      productGridContainer: document.getElementById('product-grid-container'),
      selectedCount: document.getElementById('selected-count'),
      maxItems: document.getElementById('max-items'),
      itemProgressFill: document.getElementById('item-progress-fill'),
      displayShipWeek: document.getElementById('display-ship-week'),
      itemProgress: document.getElementById('item-progress'),
      dietarySection: document.getElementById('dietary-section'),
      curationSection: document.getElementById('curation-section'),
      step2Title: document.getElementById('step2-title'),
      step2Subtitle: document.getElementById('step2-subtitle'),
      deadlineNote: document.getElementById('customize-deadline'),
      lockNote: document.getElementById('complete-lock-note'),
      // Navigation
      btnComplete: document.getElementById('btn-complete'),
      footer: document.getElementById('builder-footer')
    };
  }

  getOrderIdFromUrl() {
    const params = new URLSearchParams(window.location.search);
    return params.get('order_id') || params.get('orderId') || params.get('order');
  }

  getTokenFromUrl() {
    const params = new URLSearchParams(window.location.search);
    return params.get('token') || params.get('key') || params.get('access_token');
  }

  // ============================================================
  // API CALLS
  // ============================================================

  async apiCall(url, options = {}) {
    const maxRetries = 3;
    let lastError;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetch(url, {
          ...options,
          cache: 'no-store',
          headers: {
            'Content-Type': 'application/json',
            ...options.headers
          }
        });
        
        const data = await response.json();
        
        if (!response.ok || !data.success) {
          throw new APIError(
            data.error || 'API_ERROR',
            data.message || 'Request failed',
            response.status
          );
        }
        
        return data;
        
      } catch (error) {
        lastError = error;
        if (error.status >= 400 && error.status < 500) break;
        if (attempt < maxRetries) {
          await this.delay(Math.pow(2, attempt) * 1000);
        }
      }
    }
    
    throw lastError;
  }

  /**
   * The page's shared bootstrap (page-post-checkout-box-builder.liquid) sends the
   * one /init for this page load. Wait for its answer rather than sending our own:
   * simultaneous /init calls used to race and create duplicate sessions for one
   * order. Resolves to null when there is no bootstrap request, it failed, or it
   * has not answered within initWaitMs; the caller then sends /init itself.
   */
  async waitForBootstrapInit() {
    if (window.__bbInitData) return window.__bbInitData;
    if (!window.__bbInitPromise) return null;
    let timer;
    const timedOut = new Promise((resolve) => {
      timer = setTimeout(() => resolve(null), this.config.initWaitMs);
    });
    try {
      return (await Promise.race([window.__bbInitPromise, timedOut])) || null;
    } finally {
      clearTimeout(timer);
    }
  }

  async initSession() {
    let data = await this.waitForBootstrapInit();
    if (!data) {
      let initUrl = `${this.config.apiBaseUrl}/init?order_id=${this.config.orderId}`;
      if (this.config.orderToken) {
        initUrl += `&token=${encodeURIComponent(this.config.orderToken)}`;
      }
      data = await this.apiCall(initUrl);
      window.__bbInitData = data;
    }

    // Backend error. The bootstrap keeps error bodies as they came: ours carry
    // success:false and a code (e.g. BUNDLE_NOT_READY), Nest's own (e.g. "Order
    // not found") carry neither, so anything that is not a success is an error.
    if (!data || data.success !== true) {
      throw new APIError((data && data.error) || 'API_ERROR', (data && data.message) || 'Request failed', 422);
    }

    // If this is a TRAY order, the new PostCheckoutTrayBuilder class handles
    // it. Bail out so we don't try to render the dietary-flow UI on TRAY data.
    if (data.flowType === 'tray') {
      this.state.bailedOutForTray = true;
      return;
    }

    this.state.sessionId = data.session.sessionId;
    this.state.orderNumber = data.order.orderNumber;
    this.state.maxItems = data.session.itemCount;
    this.state.boxSize = data.session.boxSize;
    this.state.shipDate = data.session.shipDate;
    this.state.shipWeek = data.session.shipWeek;
    this.state.boxSizePricing = data.boxSizePricing || null;
    
    if (data.session.curationType) {
      this.state.curationType = data.session.curationType;
    }
    if (data.session.theme) {
      this.state.theme = data.session.theme;
    }
    
    const boxConfig = {
      size: data.session.boxSize,
      sizeLabel: this.getBoxSizeLabel(data.session.boxSize, data.session.itemCount),
      itemCount: data.session.itemCount,
      shipWeek: data.session.shipWeek || this.formatShipDate(data.session.shipDate) || 'TBD'
    };
    
    this.updateOrderInfo({ order: data.order, boxConfig });
    this.updateStep2Displays();
    this.renderDeadlineNote(data.session.expiresAt);
    this.saveToStorage();
  }

  updateStep2Displays() {
    const boxSizeOptions = document.querySelectorAll('.box-size-option');
    boxSizeOptions.forEach(option => {
      const optionSize = option.dataset.size;
      const optionItems = parseInt(option.dataset.items);
      const isSelected = optionSize === this.state.boxSize || optionItems === this.state.maxItems;
      option.classList.toggle('is-selected', isSelected);
      
      const textEl = option.querySelector('.box-size-option__text');
      if (textEl && this.state.boxSizePricing) {
        const itemKey = String(optionItems);
        const pricing = this.state.boxSizePricing[itemKey];
        if (pricing) {
          const priceFormatted = `$${pricing.price.toFixed(0)}`;
          textEl.textContent = `${optionItems} ITEMS - ${priceFormatted}`;
        }
      }
    });
    
    if (this.elements.displayShipWeek) {
      const shipDisplay = this.state.shipWeek || this.formatShipDate(this.state.shipDate) || 'TBD';
      this.elements.displayShipWeek.textContent = shipDisplay;
    }
    
    if (this.elements.maxItems) {
      this.elements.maxItems.textContent = this.state.maxItems || 0;
    }
    if (this.elements.selectedCount) {
      this.elements.selectedCount.textContent = this.getTotalSelectedItems();
    }
  }

  formatShipDate(shipDate) {
    if (!shipDate) return null;
    try {
      const date = new Date(shipDate);
      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch (e) {
      return shipDate;
    }
  }

  getBoxSizeLabel(size, itemCount) {
    const labels = {
      'M': `Medium (${itemCount} items)`,
      'L': `Large (${itemCount} items)`,
      'S': `Small (${itemCount} items)`
    };
    return labels[size] || `${size} (${itemCount} items)`;
  }

  /**
   * Put the session on Rotating Curations, then load the curations and
   * pre-fill the items for the selected one.
   * The curation type is saved first: /category checks the theme against it,
   * and it replaces a 'fixed' type saved by the old plan choice.
   */
  async enterRotatingCurations() {
    this.state.curationType = 'rotating';
    await this.apiCall(`${this.config.apiBaseUrl}/curation-type`, {
      method: 'POST',
      body: JSON.stringify({
        sessionId: this.state.sessionId,
        curationType: 'rotating'
      })
    });

    await this.loadCurationThemes();
    if (this.state.theme) {
      await this.loadCuratedItems();
    }
  }

  /**
   * Load curation themes for the curated flow.
   * Calls /variants?productType=X and populates the curation selector.
   */
  async loadCurationThemes() {
    try {
      const data = await this.apiCall(
        `${this.config.apiBaseUrl}/variants?productType=${this.state.dietary}`
      );
      
      // Store full variant data for later use (to get variant IDs)
      this.state.variantData = data.variants || {};
      
      // Get rotating themes (curated uses rotating curation type)
      const allRotatingThemes = data.themes?.rotating || [];
      
      // Only show Seasonal Special and The Monger's Table (in this order)
      // Seasonal Special is first so it is the default-selected curation.
      const allowedThemes = ["Seasonal Special", "The Monger's Table"];
      const rotatingThemes = allowedThemes.filter(t => allRotatingThemes.includes(t));
      
      this.state.availableThemes = rotatingThemes;
      
      // Auto-select first available theme, or validate existing selection
      if (rotatingThemes.length > 0) {
        if (!this.state.theme || !rotatingThemes.includes(this.state.theme)) {
          this.state.theme = rotatingThemes[0];
        }
      }
      
      this.renderCurationSelector(rotatingThemes);
      
    } catch (error) {
      console.warn('[BoxBuilder] Failed to load curation themes:', error);
    }
  }

  /**
   * Load curated preset items with editable display.
   * Uses /prefill-popular to pre-select items, but customer can modify.
   */
  async loadCuratedItems() {
    // Validate theme is set before calling API
    if (!this.state.theme || typeof this.state.theme !== 'string') {
      console.warn('[BoxBuilder] No valid theme selected, skipping loadCuratedItems');
      return;
    }
    
    this.showLoading(this.config.texts.loadingItems || 'Loading products...');
    
    try {
      // First save the theme to the session so prefill-popular uses it
      await this.apiCall(`${this.config.apiBaseUrl}/category`, {
        method: 'POST',
        body: JSON.stringify({
          sessionId: this.state.sessionId,
          theme: this.state.theme,
          category: this.state.theme,
          dietaryRestrictions: [this.state.dietary]
        })
      });
      
      // Then get preset items
      const data = await this.apiCall(`${this.config.apiBaseUrl}/prefill-popular`, {
        method: 'POST',
        body: JSON.stringify({
          sessionId: this.state.sessionId,
          dietary: this.state.dietary
        })
      });
      
      if (data.success && data.prefillItems) {
        // Also load full item details for display (cache-bust to get fresh filtered items)
        const itemsData = await this.apiCall(
          `${this.config.apiBaseUrl}/items?sessionId=${this.state.sessionId}&_t=${Date.now()}`
        );
        
        this.state.availableItems = itemsData.items || [];
        this.state.collections = itemsData.collections || [];
        
        // Render grid in editable mode (customer can add/remove)
        this.renderProductGrid(itemsData.items || [], itemsData.collections || [], false);
        
        // Apply the preset selections (pre-fill with curated items)
        this.applyItemSelection(data.prefillItems);
      }
    } catch (error) {
      console.warn('[BoxBuilder] Failed to load curated items:', error);
      this.showToast('Could not load curated items', 'error');
    }
    
    this.hideLoading();
  }

  async savePreferences() {
    this.showLoading(this.config.texts.savingPreferences || 'Saving preferences...');
    
    await this.apiCall(`${this.config.apiBaseUrl}/curation-type`, {
      method: 'POST',
      body: JSON.stringify({
        sessionId: this.state.sessionId,
        curationType: this.state.curationType
      })
    });

    await this.apiCall(`${this.config.apiBaseUrl}/category`, {
      method: 'POST',
      body: JSON.stringify({
        sessionId: this.state.sessionId,
        theme: this.state.theme,
        category: this.state.theme,
        dietaryRestrictions: [this.state.dietary]
      })
    });
    
    this.hideLoading();
  }

  async saveItems() {
    const items = this.state.selectedItems.map(item => ({
      variantId: item.variantId,
      quantity: item.quantity
    }));
    
    await this.apiCall(`${this.config.apiBaseUrl}/items`, {
      method: 'POST',
      body: JSON.stringify({
        sessionId: this.state.sessionId,
        items: items
      })
    });
  }

  /**
   * Complete curated box with customer's modified item selections.
   * Curated flow: customer chose a curation theme + dietary preference,
   * items were pre-filled, but customer may have modified them.
   * Uses /complete endpoint since items are saved as selections.
   */
  async completeCuratedCustomization() {
    this.showLoading(this.config.texts.completingCurated || 'Confirming your selection...');
    
    if (!this.state.theme) {
      this.hideLoading();
      this.showToast('Unable to determine box theme. Please try again.', 'error');
      return;
    }
    
    // Save category with the customer's actual dietary preference (not hardcoded 'standard')
    await this.apiCall(`${this.config.apiBaseUrl}/category`, {
      method: 'POST',
      body: JSON.stringify({
        sessionId: this.state.sessionId,
        theme: this.state.theme,
        category: this.state.theme,
        dietaryRestrictions: [this.state.dietary || 'standard']
      })
    });
    
    // Save the customer's (possibly modified) item selections
    await this.saveItems();
    
    // Use /complete endpoint — backend resolves the correct variant from
    // session.curationType (rotating) + session.theme + session.productType
    const data = await this.apiCall(`${this.config.apiBaseUrl}/complete`, {
      method: 'POST',
      body: JSON.stringify({
        sessionId: this.state.sessionId
      })
    });
    
    this.hideLoading();
    this.clearStorage();
    
    const redirectUrl = data.redirectUrl || this.config.curatedRedirectUrl;
    window.location.href = redirectUrl;
  }

  // ============================================================
  // EVENT HANDLERS
  // ============================================================

  setupEventListeners() {
    // Dietary preference (Step 2)
    this.elements.dietaryInputs.forEach(input => {
      input.addEventListener('change', (e) => this.handleDietaryChange(e));
    });

    // Navigation buttons
    if (this.elements.btnComplete) {
      this.elements.btnComplete.addEventListener('click', () => this.handleComplete());
    }

    // The lock-in note can wrap on narrow screens; keep the lifted offsets in step
    window.addEventListener('resize', () => {
      if (this.elements.lockNote && !this.elements.lockNote.classList.contains('hidden')) {
        this.syncLockNote(true);
      }
    });

    // Handle preference pill styling
    document.querySelectorAll('.preference-pill').forEach(option => {
      option.addEventListener('click', () => {
        const input = option.querySelector('input');
        if (input) {
          const group = option.closest('.preference-pills');
          if (group) {
            group.querySelectorAll('.preference-pill').forEach(opt => {
              opt.classList.toggle('is-selected', opt.querySelector('input').checked);
            });
          }
        }
      });
    });
  }

  handleDietaryChange(e) {
    this.state.dietary = e.target.value;
    
    const container = e.target.closest('.preference-pills');
    if (container) {
      container.querySelectorAll('.preference-pill').forEach(opt => {
        opt.classList.toggle('is-selected', opt.querySelector('input').checked);
      });
    }
    
    // Reload curation themes for the new dietary preference
    // Keep current theme selection — loadCurationThemes will validate it
    this.loadCurationThemes().then(() => {
      // Auto-load items for the selected theme
      if (this.state.theme) {
        this.loadCuratedItems();
      }
    });

    this.saveToStorage();
  }

  /**
   * Handle curation card selection (curated flow).
   * Auto-fills items from the selected curation theme.
   */
  handleCurationSelect(themeCode) {
    if (!themeCode || typeof themeCode !== 'string') {
      console.warn('[BoxBuilder] Invalid theme code:', themeCode);
      return;
    }
    
    this.state.theme = themeCode;
    
    // Load preset items for this curation
    this.loadCuratedItems();
    
    this.saveToStorage();
  }

  /**
   * Handle complete button.
   */
  async handleComplete() {
    if (this.state.isLoading || this.state.isCompleting) return;
    this.state.isCompleting = true;
    this.elements.btnComplete.disabled = true;

    try {
      // Validate selection
      const totalSelected = this.getTotalSelectedItems();
      if (totalSelected < this.state.maxItems) {
        this.showToast(`Please select ${this.state.maxItems} items (${totalSelected} selected)`, 'error');
        return;
      }

      await this.completeCuratedCustomization();
    } catch (error) {
      console.error('[BoxBuilder] Error completing:', error);
      this.state.isCompleting = false;
      // The window closed, or the box was completed elsewhere, while this page
      // was open. Nothing more can be saved, so explain instead of a toast.
      if (this.refusalCopy(error.code)) {
        this.showError(error.code, error.message);
        return;
      }
      this.elements.btnComplete.disabled = false;
      this.hideLoading();
      this.showToast(error.message || 'Something went wrong', 'error');
    }
  }

  // ============================================================
  // STEP NAVIGATION
  // ============================================================

  /**
   * Configure Step 2 UI for Rotating Curations.
   */
  configureStep2() {
    // Update titles
    if (this.elements.step2Title) {
      this.elements.step2Title.textContent = 'Rotating Curations - Selection';
    }
    if (this.elements.step2Subtitle) {
      this.elements.step2Subtitle.textContent = 'Choose your preferences and we\'ll curate the perfect selection for you.';
    }
  }

  showStep(stepNumber) {
    const step = parseInt(stepNumber, 10) || 2;

    document.querySelectorAll('.post-checkout-step').forEach(stepEl => {
      stepEl.classList.add('hidden');
    });

    const targetStep = document.querySelector(`.post-checkout-step[data-step="${step}"]`);
    if (targetStep) {
      targetStep.classList.remove('hidden');
    } else {
      console.warn(`[BoxBuilder] Step ${step} not found`);
    }

    this.state.currentStep = step;
    this.updateNavigationButtons();
    this.saveToStorage();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  updateNavigationButtons() {
    const { btnComplete } = this.elements;

    if (this.state.currentStep === 2) {
      if (btnComplete) btnComplete.style.display = 'inline-flex';

      // Enable complete when enough items are selected
      const totalSelected = this.getTotalSelectedItems();
      if (btnComplete) {
        btnComplete.disabled = totalSelected < this.state.maxItems;
        btnComplete.textContent = totalSelected < this.state.maxItems 
          ? `Select ${this.state.maxItems - totalSelected} more items` 
          : 'Complete My Box';
      }
    }

    this.syncLockNote(this.state.currentStep === 2);
  }

  // ============================================================
  // RENDERING
  // ============================================================

  updateOrderInfo(data) {
    if (this.elements.orderNumber && data.order) {
      this.elements.orderNumber.textContent = data.order.orderNumber || 'Order';
    }
    if (this.elements.boxSizeLabel && data.boxConfig) {
      this.elements.boxSizeLabel.textContent = data.boxConfig.sizeLabel || data.boxConfig.size || '';
    }
    if (this.elements.shipWeek && data.boxConfig) {
      this.elements.shipWeek.textContent = data.boxConfig.shipWeek || 'TBD';
    }
    if (this.elements.maxItems && data.boxConfig) {
      this.elements.maxItems.textContent = data.boxConfig.itemCount || 0;
    }
  }

  getTotalSelectedItems() {
    if (!this.state.selectedItems || !Array.isArray(this.state.selectedItems)) return 0;
    return this.state.selectedItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
  }

  /**
   * Render the curation selector cards for curated flow.
   */
  renderCurationSelector(themes) {
    const container = this.elements.curationSelector;
    if (!container) return;
    
    if (!themes || themes.length === 0) {
      container.innerHTML = '<p class="curation-cards__empty">No curations available</p>';
      return;
    }
    
    container.innerHTML = themes.map(themeName => {
      const isSelected = themeName === this.state.theme;
      const description = this.config.themeDescriptions[themeName] || '';
      
      return `
        <div class="curation-card ${isSelected ? 'is-selected' : ''}" data-theme="${themeName}">
          <label>
            <input type="radio" name="curation" value="${themeName}" ${isSelected ? 'checked' : ''} style="display:none">
            <div class="curation-card__header">
              <span class="curation-card__radio"></span>
              <span class="curation-card__title">${themeName}</span>
            </div>
            ${description ? `<p class="curation-card__description">${description}</p>` : ''}
          </label>
        </div>
      `;
    }).join('');
    
    // Add event listeners
    container.querySelectorAll('.curation-card').forEach(card => {
      card.addEventListener('click', () => {
        const input = card.querySelector('input[name="curation"]');
        if (input) {
          input.checked = true;
          // Update card styling
          container.querySelectorAll('.curation-card').forEach(c => {
            c.classList.toggle('is-selected', c.querySelector('input').checked);
          });
          this.handleCurationSelect(input.value);
        }
      });
    });
  }

  /**
   * Fallback priority for client-side collection ordering when the server does
   * not provide a collections list: Cheeses → Charcuterie → Pairings.
   * Keyword match handles both base ("Artisan Cheeses") and Monthly Special
   * ("Artisan Cheeses [Monthly Special]") collection names. Unknown collections
   * sort last (priority 99) and keep their original relative order via a stable
   * sort. When the server DOES provide collections, its admin-configured order
   * is used as-is and this priority is ignored.
   */
  collectionSortPriority(name) {
    const n = String(name || '').toLowerCase();
    if (n.includes('cheese')) return 0;
    if (n.includes('charcuterie')) return 1;
    if (n.includes('pairing')) return 2;
    return 99;
  }

  /**
   * Render the product grid grouped by collection.
   * @param {Array} items - Flat array of product items
   * @param {Array} collections - Array of collection objects {id, name}
   * @param {boolean} readOnly - If true, no add/remove buttons (curated mode)
   */
  renderProductGrid(items, collections = [], readOnly = false) {
    const grid = this.elements.productGrid;
    if (!grid) {
      console.warn('[BoxBuilder] Product grid element not found');
      return;
    }
    
    if (!items || items.length === 0) {
      grid.innerHTML = '<div class="product-grid__empty">No products available for selection</div>';
      return;
    }
    
    // Get unique collections. When the server provides the list, its order is
    // admin-configured (Box Builder Settings → collection order) — respect it as-is.
    // Only when deriving collections from items (no server list) fall back to the
    // keyword priority sort: Cheeses → Charcuterie → Pairings.
    const uniqueCollections = collections.length > 0
      ? [...collections]
      : [...new Map(items.map(i => [i.collectionId, { id: i.collectionId, name: i.collectionName }])).values()].sort(
          (a, b) => this.collectionSortPriority(a.name || a) - this.collectionSortPriority(b.name || b)
        );
    
    // Group items by collection
    const groupedItems = new Map();
    for (const item of items) {
      const collId = item.collectionId || 'uncategorized';
      if (!groupedItems.has(collId)) {
        groupedItems.set(collId, []);
      }
      groupedItems.get(collId).push(item);
    }
    
    // Render grouped layout
    let html = '';
    
    for (const collection of uniqueCollections) {
      const collId = collection.id || collection;
      const collName = collection.name || collection;
      const collItems = groupedItems.get(collId) || groupedItems.get(String(collId));
      
      if (!collItems || collItems.length === 0) continue;
      
      html += `
        <div class="collection-group" data-collection-id="${collId}">
          <h3 class="collection-group__title">${collName}</h3>
          <div class="collection-group__grid">
            ${collItems.map(product => this.renderProductCard(product, readOnly)).join('')}
          </div>
        </div>
      `;
    }
    
    // If no collections matched, render all items in a flat grid
    if (!html) {
      html = `
        <div class="collection-group">
          <div class="collection-group__grid">
            ${items.map(product => this.renderProductCard(product, readOnly)).join('')}
          </div>
        </div>
      `;
    }
    
    grid.innerHTML = html;
    
    // Clear stale selected items state since we just rendered a fresh grid
    this.state.selectedItems = [];
    
    // Add event listeners to product cards
    this.setupProductCardListeners();
    
    this.updateItemProgress();
  }

  /**
   * Render a single product card.
   */
  renderProductCard(product, readOnly = false) {
    const actionsHtml = readOnly
      ? '' // No actions for curated (read-only)
      : (product.inStock !== false ? `
          <div class="product-card__actions">
            <button type="button" class="product-card__add-btn">+ Add</button>
            <div class="product-card__quantity" style="display: none;">
              <button type="button" class="qty-btn decrease" disabled>−</button>
              <input type="number" value="0" min="0" max="${product.maxQuantity || 3}" readonly>
              <button type="button" class="qty-btn increase">+</button>
            </div>
          </div>
        ` : `
          <div class="product-card__actions">
            <span class="product-card__unavailable-text">Unavailable</span>
          </div>
        `);

    const hasProductInfo = product.description || product.customerPortalDescription;
    const infoIconHtml = hasProductInfo ? `
      <button type="button" class="product-card__info-icon" aria-label="Product info">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><path d="M50,7A43,43,0,1,0,93,50,43,43,0,0,0,50,7Zm6.93,68.29a6.68,6.68,0,0,1-6.88,6.45,7.1,7.1,0,0,1-4.86-1.89,6.25,6.25,0,0,1-2-4.56V45.72a6.67,6.67,0,0,1,6.87-6.44,7.14,7.14,0,0,1,4.87,1.88,6.25,6.25,0,0,1,2,4.56ZM50,33.05a7.4,7.4,0,1,1,7.39-7.4A7.39,7.39,0,0,1,50,33.05Z"/></svg>
      </button>
    ` : '';

    return `
      <div class="product-card ${!product.inStock && product.inStock !== undefined ? 'product-card--unavailable' : ''} ${readOnly ? 'product-card--readonly' : ''} ${product.pcHide ? 'product-card--pc-hidden' : ''}"
           data-product-id="${product.productId}"
           data-variant-id="${product.variantId}"
           data-collection-id="${product.collectionId || ''}"
           data-pc-hide="${product.pcHide ? 'true' : 'false'}"
           data-max-qty="${product.maxQuantity || 3}">
        <div class="product-card__image">
          ${product.imageUrl
            ? `<img src="${product.imageUrl}" alt="${product.title}" loading="lazy">`
            : '<div class="product-card__image-placeholder"></div>'
          }
          ${product.inStock === false ? '<div class="product-card__unavailable-badge">Out of Stock</div>' : ''}
          ${infoIconHtml}
        </div>
        <div class="product-card__info">
          <h4 class="product-card__title">${product.title}</h4>
          ${product.variantTitle && product.variantTitle !== 'Default Title' ? `<p class="product-card__variant">${product.variantTitle}</p>` : ''}
        </div>
        ${actionsHtml}
      </div>
    `;
  }

  setupProductCardListeners() {
    const grid = this.elements.productGrid;

    grid.querySelectorAll('.product-card:not(.product-card--readonly)').forEach(card => {
      const addBtn = card.querySelector('.product-card__add-btn');
      const qtyContainer = card.querySelector('.product-card__quantity');
      const increaseBtn = card.querySelector('.increase');
      const decreaseBtn = card.querySelector('.decrease');

      if (addBtn) {
        addBtn.addEventListener('click', () => {
          addBtn.style.display = 'none';
          qtyContainer.style.display = 'flex';
          this.adjustQuantity(card, 1);
        });
      }

      if (increaseBtn) {
        increaseBtn.addEventListener('click', () => this.adjustQuantity(card, 1));
      }

      if (decreaseBtn) {
        decreaseBtn.addEventListener('click', () => this.adjustQuantity(card, -1));
      }
    });

    // Info icon click handlers
    grid.querySelectorAll('.product-card__info-icon').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const card = btn.closest('.product-card');
        const variantId = card.getAttribute('data-variant-id');
        const product = this.state.availableItems.find(item => item.variantId === variantId);
        if (product) this.createProductInfoPopup(product);
      });
    });
  }

  /**
   * Create and display a product info popup modal.
   */
  createProductInfoPopup(product) {
    // Remove any existing popup
    const existing = document.querySelector('.product-info-popup');
    if (existing) existing.remove();

    // Use description as primary content; fall back to customerPortalDescription
    // if no description exists (description typically already includes ingredients + allergens)
    let descriptionHtml = '';
    if (product.description) {
      descriptionHtml = product.description;
    } else if (product.customerPortalDescription) {
      try {
        const richText = JSON.parse(product.customerPortalDescription);
        descriptionHtml = this.renderRichText(richText);
      } catch {
        descriptionHtml = product.customerPortalDescription;
      }
    }

    const popup = document.createElement('div');
    popup.className = 'product-info-popup';
    popup.innerHTML = `
      <div class="product-info-popup-content">
        <button class="product-info-popup-close">&times;</button>
        <h2>${product.title}</h2>
        <div class="product-info-popup-columns">
          ${product.imageUrl ? `
            <div class="product-info-popup-image">
              <img src="${product.imageUrl}" alt="${product.title}">
            </div>
          ` : ''}
          <div class="product-info-popup-description">
            ${descriptionHtml}
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(popup);
    document.body.classList.add('popup-open');
    document.documentElement.classList.add('html-popup-open');

    const closePopup = () => {
      popup.remove();
      document.body.classList.remove('popup-open');
      document.documentElement.classList.remove('html-popup-open');
      document.removeEventListener('keydown', escHandler);
    };

    popup.querySelector('.product-info-popup-close').addEventListener('click', closePopup);
    popup.addEventListener('click', (e) => {
      if (e.target === popup) closePopup();
    });

    const escHandler = (e) => {
      if (e.key === 'Escape') closePopup();
    };
    document.addEventListener('keydown', escHandler);
  }

  /**
   * Render a Shopify rich_text_field JSON to HTML.
   */
  renderRichText(node) {
    if (!node) return '';
    if (typeof node === 'string') return node;

    if (node.type === 'text') {
      let text = (node.value || '').replace(/\n/g, '<br>');
      if (node.bold) text = `<strong>${text}</strong>`;
      if (node.italic) text = `<em>${text}</em>`;
      return text;
    }

    const children = (node.children || []).map(child => this.renderRichText(child)).join('');

    switch (node.type) {
      case 'root': return children;
      case 'paragraph': return `<p>${children}</p>`;
      case 'heading': return `<h${node.level || 3}>${children}</h${node.level || 3}>`;
      case 'list':
        const tag = node.listType === 'ordered' ? 'ol' : 'ul';
        return `<${tag}>${children}</${tag}>`;
      case 'list-item': return `<li>${children}</li>`;
      case 'link': return `<a href="${node.url || '#'}" target="_blank" rel="noopener">${children}</a>`;
      default: return children;
    }
  }

  adjustQuantity(card, delta) {
    const input = card.querySelector('input');
    const addBtn = card.querySelector('.product-card__add-btn');
    const qtyContainer = card.querySelector('.product-card__quantity');
    const currentValue = parseInt(input.value) || 0;
    const maxValue = parseInt(input.max) || 3;
    const newValue = Math.max(0, Math.min(maxValue, currentValue + delta));
    
    const totalSelected = this.getTotalSelectedCount();
    if (delta > 0 && totalSelected >= this.state.maxItems) {
      this.showToast(`Maximum ${this.state.maxItems} items allowed`, 'warning');
      return;
    }
    
    input.value = newValue;
    
    card.querySelector('.decrease').disabled = newValue === 0;
    card.querySelector('.increase').disabled = newValue >= maxValue || totalSelected >= this.state.maxItems - 1;
    
    if (newValue === 0) {
      addBtn.style.display = 'block';
      qtyContainer.style.display = 'none';
      card.classList.remove('active');
    } else {
      card.classList.add('active');
    }
    
    this.updateSelectedItems();
    this.updateItemProgress();
    this.saveToStorage();
  }

  getTotalSelectedCount() {
    let total = 0;
    document.querySelectorAll('.product-card input').forEach(input => {
      total += parseInt(input.value) || 0;
    });
    return total;
  }

  updateSelectedItems() {
    this.state.selectedItems = [];
    document.querySelectorAll('.product-card.active').forEach(card => {
      const quantity = parseInt(card.querySelector('input').value) || 0;
      if (quantity > 0) {
        this.state.selectedItems.push({
          productId: card.dataset.productId,
          variantId: card.dataset.variantId,
          quantity,
          title: card.querySelector('.product-card__title').textContent
        });
      }
    });
  }

  updateItemProgress() {
    const total = this.getTotalSelectedItems();
    
    if (this.elements.selectedCount) {
      this.elements.selectedCount.textContent = total;
    }
    if (this.elements.maxItems) {
      this.elements.maxItems.textContent = this.state.maxItems;
    }
    
    const fillPercent = Math.min(100, (total / this.state.maxItems) * 100);
    if (this.elements.itemProgressFill) {
      this.elements.itemProgressFill.style.width = `${fillPercent}%`;
      this.elements.itemProgressFill.classList.toggle('complete', total >= this.state.maxItems);
    }
    
    this.updateNavigationButtons();
    
    // Manage disabled state for both flows
    if (total >= this.state.maxItems) {
      document.querySelectorAll('.product-card:not(.active)').forEach(card => {
        card.classList.add('disabled');
      });
      document.querySelectorAll('.product-card .increase').forEach(btn => {
        btn.disabled = true;
      });
    } else {
      document.querySelectorAll('.product-card.disabled').forEach(card => {
        card.classList.remove('disabled');
      });
      document.querySelectorAll('.product-card.active .increase').forEach(btn => {
        const card = btn.closest('.product-card');
        const input = card.querySelector('input');
        btn.disabled = parseInt(input.value) >= parseInt(input.max);
      });
    }
  }

  applyItemSelection(items) {
    // Match preset items to displayed cards and mark them as selected
    
    // Clear existing selections
    document.querySelectorAll('.product-card.active').forEach(card => {
      const input = card.querySelector('input');
      const addBtn = card.querySelector('.product-card__add-btn');
      const qtyContainer = card.querySelector('.product-card__quantity');
      
      if (input) input.value = 0;
      card.classList.remove('active');
      if (addBtn) addBtn.style.display = 'block';
      if (qtyContainer) qtyContainer.style.display = 'none';
    });
    
    // Apply new selections
    const selectedItems = [];
    items.forEach(item => {
      const card = document.querySelector(`[data-variant-id="${item.variantId}"]`);
      if (card && !card.classList.contains('product-card--unavailable')) {
        const input = card.querySelector('input');
        const addBtn = card.querySelector('.product-card__add-btn');
        const qtyContainer = card.querySelector('.product-card__quantity');
        
        if (input) input.value = item.quantity;
        card.classList.add('active');
        if (addBtn) addBtn.style.display = 'none';
        if (qtyContainer) qtyContainer.style.display = 'flex';
        
        const decreaseBtn = card.querySelector('.decrease');
        if (decreaseBtn) decreaseBtn.disabled = item.quantity === 0;
        
        selectedItems.push({
          productId: card.dataset.productId,
          variantId: item.variantId,
          quantity: item.quantity,
          title: card.querySelector('.product-card__title')?.textContent || ''
        });
      }
    });
    
    this.state.selectedItems = selectedItems;
    this.updateItemProgress();
    this.saveToStorage();
  }

  // ============================================================
  // SESSION PERSISTENCE
  // ============================================================

  saveToStorage() {
    const persistState = {
      sessionId: this.state.sessionId,
      theme: this.state.theme,
      dietary: this.state.dietary,
      selectedItems: this.state.selectedItems,
      boxSize: this.state.boxSize,
      maxItems: this.state.maxItems,
      lastSavedAt: new Date().toISOString()
    };
    try {
      sessionStorage.setItem('boxBuilderState', JSON.stringify(persistState));
    } catch (e) {
      console.warn('Failed to save state to sessionStorage:', e);
    }
  }

  restoreFromStorage() {
    try {
      const saved = sessionStorage.getItem('boxBuilderState');
      if (saved) {
        const parsed = JSON.parse(saved);
        
        const savedTime = new Date(parsed.lastSavedAt);
        const now = new Date();
        const hoursDiff = (now - savedTime) / (1000 * 60 * 60);
        
        if (hoursDiff < 4) {
          Object.assign(this.state, {
            theme: parsed.theme,
            dietary: parsed.dietary || 'standard',
            selectedItems: parsed.selectedItems || []
          });
          return parsed;
        }
      }
    } catch (e) {
      console.warn('Failed to restore state from sessionStorage:', e);
    }
    return null;
  }

  clearStorage() {
    try {
      sessionStorage.removeItem('boxBuilderState');
    } catch (e) {
      console.warn('Failed to clear sessionStorage:', e);
    }
  }

  // ============================================================
  // UI HELPERS
  // ============================================================

  showLoading(message = 'Loading...') {
    this.state.isLoading = true;
    if (this.elements.loadingOverlay) {
      this.elements.loadingOverlay.classList.add('is-visible');
    }
    if (this.elements.loadingMessage) {
      this.elements.loadingMessage.textContent = message;
    }
  }

  hideLoading() {
    this.state.isLoading = false;
    if (this.elements.loadingOverlay) {
      this.elements.loadingOverlay.classList.remove('is-visible');
    }
  }

  showError(code, message) {
    this.state.error = code;
    this.hideLoading();

    document.querySelectorAll('.post-checkout-step').forEach(step => {
      step.classList.add('hidden');
    });

    if (this.elements.footer) {
      this.elements.footer.style.display = 'none';
    }

    // Nothing on the page can be saved any more, so the time and lock-in notes go too
    if (this.elements.deadlineNote) {
      this.elements.deadlineNote.classList.add('hidden');
    }
    this.syncLockNote(false);

    const refusal = this.refusalCopy(code);

    if (this.elements.errorState) {
      this.elements.errorState.classList.remove('hidden');
    }
    if (this.elements.errorMessage) {
      this.elements.errorMessage.textContent = refusal ? refusal.message : message;
    }

    // Per-code overrides. Defaults stay in place for every other error so we
    // don't lose context for unrelated failures.
    const titleEl = document.querySelector('.error-state__title');
    const iconEl = this.elements.errorState
      ? this.elements.errorState.querySelector('.error-state__icon')
      : null;
    const ctaEl = this.elements.errorState
      ? this.elements.errorState.querySelector('a.button')
      : null;
    let subTextEl = this.elements.errorState
      ? this.elements.errorState.querySelector('.error-state__subtext')
      : null;

    if (code === 'BUNDLE_NOT_READY') {
      if (titleEl) {
        titleEl.textContent = `We're still creating your order!`;
      }
      if (iconEl) {
        iconEl.textContent = '⏳';
      }
      if (ctaEl) {
        if (!ctaEl.dataset.defaultHref) {
          ctaEl.dataset.defaultHref = ctaEl.getAttribute('href') || '';
          ctaEl.dataset.defaultText = ctaEl.textContent;
        }
        ctaEl.textContent = 'Refresh';
        ctaEl.setAttribute('href', '#');
        ctaEl.onclick = (e) => {
          e.preventDefault();
          window.location.reload();
        };
      }
      if (!subTextEl && ctaEl) {
        subTextEl = document.createElement('p');
        subTextEl.className = 'error-state__subtext';
        subTextEl.innerHTML = `Still unable to customize your box after 10 minutes? Please contact our support team <a href="https://appyhourbox.gorgias.help/en-US/contact" target="_blank" rel="noopener">here</a> for assistance.`;
        ctaEl.insertAdjacentElement('afterend', subTextEl);
      }
      if (subTextEl) {
        subTextEl.classList.remove('hidden');
      }
    } else {
      if (titleEl) {
        titleEl.textContent = refusal ? refusal.title : 'Something went wrong';
      }
      if (iconEl) {
        iconEl.textContent = refusal ? refusal.icon : '⚠️';
      }
      if (ctaEl && ctaEl.dataset.defaultHref) {
        ctaEl.setAttribute('href', ctaEl.dataset.defaultHref);
        ctaEl.textContent = ctaEl.dataset.defaultText || 'Contact Customer Support';
        ctaEl.onclick = null;
      }
      if (subTextEl) {
        subTextEl.classList.add('hidden');
      }
    }

    // No automatic reload for a closed window: reloading only gets the same answer
    if (code === 'ORDER_NOT_ELIGIBLE') {
      setTimeout(() => window.location.href = '/account', 3000);
    }
  }

  /**
   * Plain-language screen for the two refusals a returning customer can get
   * from the backend, or null for any other error. The copy is editable on the
   * section; these strings are the fallbacks.
   */
  refusalCopy(code) {
    const texts = this.config.texts;
    if (code === 'CUSTOMIZE_WINDOW_CLOSED') {
      return {
        icon: '⏰',
        title: texts.windowClosedTitle || 'Your customization window has closed',
        message: texts.windowClosedMessage || "The time to customize this order has passed, so your box will ship with the items already in your order. Need a change? Contact our support team and we'll help."
      };
    }
    if (code === 'ALREADY_CUSTOMIZED') {
      return {
        icon: '✅',
        title: texts.alreadyCustomizedTitle || 'Your box is already customized',
        message: texts.alreadyCustomizedMessage || "You've already completed your box, so your picks are locked in for this order. Need a change? Contact our support team and we'll help."
      };
    }
    return null;
  }

  /**
   * Fill the customize-by note from /init's expiresAt, shown in the customer's
   * own time zone. The date is added only when the deadline isn't today
   * (e.g. a link support has extended). Stays hidden if the deadline is
   * missing or already past, or the note is blank in the theme editor.
   */
  renderDeadlineNote(expiresAt) {
    const el = this.elements.deadlineNote;
    const template = el ? el.dataset.template || '' : '';
    const deadline = this.formatDeadline(expiresAt);
    if (!el || !template || !deadline) return;

    el.textContent = '';
    template.split('%time%').forEach((part, i) => {
      if (i > 0) {
        const strong = document.createElement('strong');
        strong.textContent = deadline;
        el.appendChild(strong);
      }
      el.appendChild(document.createTextNode(part));
    });
    el.classList.remove('hidden');
  }

  formatDeadline(expiresAt) {
    if (!expiresAt) return null;
    const deadline = new Date(expiresAt);
    if (isNaN(deadline.getTime()) || deadline.getTime() <= Date.now()) return null;
    const time = deadline.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
    if (deadline.toDateString() === new Date().toDateString()) return time;
    const day = deadline.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    return `${day}, ${time}`;
  }

  /**
   * Show or hide the lock-in note above the Complete button. The note makes the
   * fixed footer taller, so the floating item counter and toasts are lifted by
   * the footer's measured height (--pc-footer-height) while it shows.
   */
  syncLockNote(visible) {
    const { lockNote, footer } = this.elements;
    if (!lockNote) return;
    lockNote.classList.toggle('hidden', !visible);
    document.body.classList.toggle('pc-footer-note-visible', visible);
    if (visible && footer) {
      document.documentElement.style.setProperty('--pc-footer-height', `${footer.offsetHeight}px`);
    }
  }

  showToast(message, type = 'info') {
    document.querySelectorAll('.toast-notification').forEach(t => t.remove());
    
    const toast = document.createElement('div');
    toast.className = `toast-notification toast-notification--${type}`;
    toast.innerHTML = `
      <span class="toast-notification__message">${message}</span>
      <button type="button" class="toast-notification__close" onclick="this.parentElement.remove()">×</button>
    `;
    document.body.appendChild(toast);
    
    setTimeout(() => {
      toast.classList.add('toast-notification--hiding');
      setTimeout(() => toast.remove(), 300);
    }, 4000);
  }

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Custom Error class
class APIError extends Error {
  constructor(code, message, status) {
    super(message);
    this.code = code;
    this.status = status;
    this.name = 'APIError';
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', function() {
  const builderContainer = document.querySelector('.post-checkout-box-builder');
  if (builderContainer) {
    const config = window.postCheckoutConfig || {
      apiBaseUrl: builderContainer.dataset.apiUrl || '/apps/box-builder'
    };
    window.postCheckoutBoxBuilder = new PostCheckoutBoxBuilder(config);
  }
});
